<template>
  <li :id="`cart-item-${entry.entryNumber}`" class="py-3 border-gray-10 border-t grid gap-4 lg:grid-cols-2">
    <pdl-media-object element="article" :aria-describedby="productIdForAria" :qaid="`cart-item-${entry.product.code}`">
      <template #media>
        <a qaid="cart-product-image" :href="entry.product.url">
          <image-component
            :alt-text="thumbnailAsset.altText"
            :title="thumbnailAsset.altText"
            aspect-ratio="4x3"
            sizes="auto"
            :asset-id="productImage"
            img-format="pjpg"
            transformation="pad"
          />
        </a>
      </template>
      <template #content>
        <div class="space-y-3">
          <div qaid="cart-product-summary">
            <h3 :id="productIdForAria" qaid="cart-product-name" class="font-bold text-md">{{ entry.product.name }}</h3>
            <pdl-label-group v-if="labelList.length" qaid="cart-product-labels">
              <pdl-label v-for="(label, i) of labelList" :key="`product-label-${i}`" :kind="label.kind">
                {{ label.content }}
              </pdl-label>
            </pdl-label-group>
            <div qaid="cart-product-details" class="text-gray-60 my-1 text-sm">
              <p
                v-for="(category, i) of entry.product.variantMatrix"
                :key="`category-${i}`"
                :qaid="`category-${getCategoryQaid(category)}`"
              >
                {{ `${category.parentVariantCategory.name}: ${category.variantValueCategory.name}` }}
              </p>
              <p :qaid="`cart-entry-${entry.entryNumber}-product-quantity`">
                {{ `${$t('order.quantity')}: ${entry.quantity}` }}
              </p>
            </div>
            <cart-item-pricing :entry="entry" :is-promotion-applied="isPromotionApplied" />
          </div>
        </div>
      </template>
    </pdl-media-object>
    <checkout-fulfillment-choice
      v-if="!isRestrictedToRetailer"
      :entry="entry"
      :retailer-for-delivery="retailerForDelivery"
      :is-checkout-confirmation="isCheckoutConfirmation"
    />
  </li>
</template>

<script>
import ImageComponent from '@/components/ImageComponent.vue';
import CheckoutFulfillmentChoice from '@/components/cart/b2c/CheckoutFulfillmentChoice.vue';
import CartItemPricing from '@/components/cart/b2c/CartItemPricing.vue';
import {PdlLabel, PdlLabelGroup} from '@pedal/pdl-label';
import {PdlMediaObject} from '@pedal/pdl-media-object';
import {mapState, mapGetters} from 'vuex';

const getVariantCategories = (product) => {
  return product.variantMatrix?.reduce((object, element, i) => {
    if (i === 0) {
      object.productColor = element.variantValueCategory?.name;
      return object;
    }
    object.productSize = element.variantValueCategory?.name;
    return object;
  }, {});
};

export default {
  components: {
    ImageComponent,
    PdlLabel,
    PdlMediaObject,
    PdlLabelGroup,
    CartItemPricing,
    CheckoutFulfillmentChoice,
  },
  props: {
    entry: {
      type: Object,
      required: true,
    },
    retailerForDelivery: {
      type: Object,
      default: () => ({}),
    },
    isECommAvailable: {
      type: Boolean,
      default: false,
    },
    isQuantityControlEnabled: {
      type: Boolean,
      default: false,
    },
    isCheckoutConfirmation: {
      type: Boolean,
      default: false,
    },
    hasMixedPromoItems: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('backend', ['currencyIso', 'azureCdnImagesUrl']),
    ...mapGetters('checkout', ['isRestrictedToRetailer']),

    itemData() {
      return {
        productCode: this.entry.product.code,
        productPrice: this.entry.basePrice.value,
        productCategory: this.entry.product.defaultCategory,
        productName: this.entry.product.name,
        productBrand: this.entry.product.brandNameFull,
        productColor: getVariantCategories(this.entry.product).productColor,
        productSize: getVariantCategories(this.entry.product).productSize,
        isoCode: this.currencyIso,
      };
    },
    isUpdateEnabled() {
      return !this.entry.product.retailerOnly && this.isECommAvailable && this.entry.updateable;
    },
    thumbnailAsset() {
      return this.entry?.product?.images[0];
    },
    productImage() {
      const missingProductImage = `${this.azureCdnImagesUrl}/missing-product-300x300.jpg`;
      const url = this.thumbnailAsset?.url ?? '';
      return url.length > 0 ? url : missingProductImage;
    },
    labelList() {
      const convertLabelList = (labels) => {
        if (!labels) return;
        return labels.map(({kind, messageKey}) => ({kind, content: this.$t(messageKey)}));
      };

      const labelList = convertLabelList(this.entry.product?.labels) ?? [];
      if (this.isPromotionApplied) {
        labelList.push({kind: 'positive', content: this.$t('cart.page.promo.applied')});
      }

      if (!this.isPromotionApplied && this.hasMixedPromoItems) {
        labelList.push({kind: 'critical', content: this.$t('basket.couponCode.excluded')});
      }

      return labelList;
    },
    isPromotionApplied() {
      return Boolean(this.entry.isLineLevelPromotionPresent);
    },
    isCartPage() {
      return window.location.href.includes('/cart');
    },
    productIdForAria() {
      return this.entry?.product?.name.trim().toLowerCase().replace(/\s+/g, '-') + '-details';
    },
  },
  methods: {
    getCategoryQaid(category) {
      return category.parentVariantCategory.code ?? category.parentVariantCategory.name;
    },
  },
};
</script>

<style lang="scss" scoped>
.cart-item-container {
  &:last-of-type {
    border-bottom-width: 1px;
  }
}

:deep(.pdl-media-object__media) {
  width: 10rem;
}
</style>
